import {ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, Input, signal} from '@angular/core';
import {NgxChartsModule} from '@swimlane/ngx-charts';
import {NgxDatum, toNgxData} from '@/app/utils/ngx-charts';
import {CommonModule} from '@angular/common';

export type DonutDatum = { name: string; value: number };
export type NgxColor = { name: string; value: string };

@Component({
  selector: 'app-donut-chart',
  standalone: true,
  imports: [NgxChartsModule, CommonModule],
  templateUrl: './donut-chart.component.html',
  styleUrls: ['./donut-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DonutChartComponent {
  constructor(private cdr: ChangeDetectorRef) {}

  @Input() arcWidth = 0.2;
  @Input() view: [number, number] = [200, 200];
  @Input() showLegend = false;
  @Input() customColors: NgxColor[] = [];

  tooltipVisible = false;
  tooltipText = '';
  tooltipX = 0;
  tooltipY = 0;
  currentMouseX = 0;
  currentMouseY = 0;
  lastActivateEvent: any = null;

  private _raw = signal<unknown[]>([]);
  @Input() set data(v: unknown[] | null) { this._raw.set(v ?? []); }

  readonly normalized = computed<NgxDatum[]>(() => {
    const rawData = toNgxData(this._raw()).filter(d => d.value > 0);
    return rawData.map((item, index) => ({
      name: `${String(item.name || 'Desconocido')}_${index}`,
      value: item.value,
    }));
  });

  readonly finalColors = computed<NgxColor[]>(() => {
    const normalizedData = this.normalized();
    return normalizedData.map(item => {
      const originalName = item.name.substring(0, item.name.lastIndexOf('_'));
      const color = this.customColors.find(c => c.name === originalName);
      return {
        name: item.name,
        value: color ? color.value : '#000000'
      };
    });
  });

  readonly viewWidth  = computed(() => this.view?.[0] ?? 200);
  readonly viewHeight = computed(() => this.view?.[1] ?? 200);
  readonly viewTuple  = computed<[number, number]>(() => [this.viewWidth(), this.viewHeight()]);

  onActivate(event: any) {
    this.lastActivateEvent = event;
    const value = event.value?.value || 0;
    let originalName = 'Desconocido';
    if (event.value?.name && event.value.name.includes('_')) {
      originalName = event.value.name.split('_')[0];
    } else if (event.value?.name) {
      originalName = event.value.name;
    } else if (event.value?.label) {
      originalName = event.value.label;
    }
    const total = this.normalized().reduce((sum, item) => sum + (Number(item.value) || 0), 0);
    const percentage = total > 0 ? Math.round((Number(value) / total) * 100) : 0;
    this.tooltipText = `${originalName}: ${value} (${percentage}%)`;
    this.tooltipX = this.currentMouseX - 50;
    this.tooltipY = this.currentMouseY - 35;
    this.tooltipVisible = true;
    this.cdr.detectChanges();
  }

  onDeactivate() {
    this.tooltipVisible = false;
    this.lastActivateEvent = null;
  }

  onChartMouseMove(event: MouseEvent) {
    this.currentMouseX = event.clientX;
    this.currentMouseY = event.clientY;
    if (this.lastActivateEvent) {
      this.tooltipX = this.currentMouseX - 50;
      this.tooltipY = this.currentMouseY - 35;
      this.cdr.detectChanges();
    }
  }
}
